import { useEffect, useRef, useState } from 'react'

// ONE shared auto-scroll for every TV / leaderboard board — the scroll analog
// of TvStage (shared fit). Renders its children TWICE (translateY 0 → -50%) for
// a seamless loop and scrolls at a CONSTANT pixels-per-second, so every board
// moves at an identical visual speed regardless of how tall it is — and they
// can't drift apart when one board is edited.
//
// Why constant px/sec: a fixed-DURATION marquee scrolls a fixed % over a fixed
// time, so its px/sec depends on content height (a short board crawls, a tall
// one races). We measure one copy's height with a ResizeObserver and derive the
// duration. Effect deps are [speed, disabled] ONLY, so live score ticks (same
// height) never re-run it → the animation is never restarted (the old stutter);
// it recomputes only on a real, >0.5s resize.
//
// Props:
//   speed          'slow' | 'medium' | 'fast' (admin tvSettings.scrollSpeed)
//   disabled       true → static single copy (admin preview tile), no animation
//   innerClassName padding/spacing for each copy, so each board keeps its look
//   style          extra styles on the outer box (e.g. fontFamily)
const PX_PER_SEC = { slow: 32, medium: 60, fast: 110 }

export default function TvScroll({
  children,
  speed = 'slow',
  disabled = false,
  innerClassName = 'px-8 py-6 space-y-6',
  style,
}) {
  const trackRef = useRef(null)
  const [duration, setDuration] = useState(20)

  useEffect(() => {
    if (disabled) return undefined
    const el = trackRef.current
    if (!el) return undefined
    const pps = PX_PER_SEC[speed] || PX_PER_SEC.slow
    const measure = () => {
      const oneCopy = el.scrollHeight / 2 // two identical stacked copies
      if (oneCopy > 0) {
        const next = Math.max(4, oneCopy / pps)
        setDuration((prev) => (Math.abs(prev - next) > 0.5 ? next : prev))
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [speed, disabled])

  if (disabled) {
    return (
      <div className="h-full overflow-hidden" style={style}>
        <div className={innerClassName}>{children}</div>
      </div>
    )
  }
  return (
    <div className="h-full overflow-hidden relative" style={style}>
      <style>{`@keyframes tv-scroll-shared { 0% { transform: translateY(0); } 100% { transform: translateY(-50%); } }`}</style>
      <div ref={trackRef} style={{ animation: `tv-scroll-shared ${duration}s linear infinite`, willChange: 'transform' }}>
        <div className={innerClassName}>{children}</div>
        <div className={innerClassName} aria-hidden="true">{children}</div>
      </div>
    </div>
  )
}
